import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { GameDate } from '@/types/events';
import type { ActiveEvent } from '@/types/dynamicEvents';
import { useNewspaperEvents } from './useNewspaperEvents';
import { NewspaperFrontPage } from './NewspaperFrontPage';
import { NewspaperSpread } from './NewspaperSpread';

interface NewspaperOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  companyName: string;
  gameDate: GameDate;
  eventHistory: ActiveEvent[];
}

export const NewspaperOverlay: React.FC<NewspaperOverlayProps> = ({
  isOpen,
  onClose,
  companyName,
  gameDate,
  eventHistory,
}) => {
  const { frontPageStory, spread1Stories, spread2Stories, totalPages } =
    useNewspaperEvents(eventHistory);

  const [currentPage, setCurrentPage] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset page when overlay opens
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(0);
      setSlideDirection(null);
      setIsAnimating(false);
    }
  }, [isOpen]);

  // Focus trap: focus the container when overlay opens
  useEffect(() => {
    if (isOpen && containerRef.current) {
      containerRef.current.focus();
    }
  }, [isOpen]);

  const goToPage = useCallback(
    (page: number, direction: 'left' | 'right') => {
      if (isAnimating || page < 0 || page >= totalPages) return;
      setSlideDirection(direction);
      setIsAnimating(true);
      // After animation, switch page
      setTimeout(() => {
        setCurrentPage(page);
        setSlideDirection(null);
        setIsAnimating(false);
      }, 300);
    },
    [isAnimating, totalPages],
  );

  const goNext = useCallback(() => {
    if (currentPage < totalPages - 1) {
      goToPage(currentPage + 1, 'left');
    }
  }, [currentPage, totalPages, goToPage]);

  const goPrev = useCallback(() => {
    if (currentPage > 0) {
      goToPage(currentPage - 1, 'right');
    }
  }, [currentPage, goToPage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      }
    },
    [onClose, goNext, goPrev],
  );

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const getSlideAnimation = () => {
    if (!slideDirection) return 'animate-newspaperOpen';
    if (slideDirection === 'left') return 'animate-pageSlideInLeft';
    return 'animate-pageSlideInRight';
  };

  const renderPage = () => {
    switch (currentPage) {
      case 0:
        return (
          <NewspaperFrontPage
            story={frontPageStory}
            companyName={companyName}
            gameDate={gameDate}
            hasMorePages={totalPages > 1}
          />
        );
      case 1:
        return (
          <NewspaperSpread
            leftStory={spread1Stories[0] ?? null}
            rightStory={spread1Stories[1] ?? null}
            pageNumbers={[2, 3]}
          />
        );
      case 2:
        return (
          <NewspaperSpread
            leftStory={spread2Stories[0] ?? null}
            rightStory={spread2Stories[1] ?? null}
            pageNumbers={[4, 5]}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="absolute inset-0 z-40 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Newspaper"
      ref={containerRef}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" aria-hidden="true" />

      {/* Newspaper container */}
      <div
        className={`relative w-full max-w-3xl mx-4 bg-amber-50 rounded shadow-2xl
          border border-amber-200/50 overflow-hidden
          ${getSlideAnimation()}`}
        style={{ minHeight: '400px', maxHeight: '80%' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-10 p-1 rounded-full
            bg-amber-100/80 hover:bg-amber-200 text-amber-800/60 hover:text-amber-900
            transition-colors duration-150"
          aria-label="Close newspaper"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Page content */}
        <div className="h-full min-h-[400px]">{renderPage()}</div>

        {/* Navigation arrows */}
        {currentPage > 0 && (
          <button
            onClick={goPrev}
            disabled={isAnimating}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full
              bg-amber-100/80 hover:bg-amber-200 text-amber-800/60 hover:text-amber-900
              transition-colors duration-150 disabled:opacity-30"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}

        {currentPage < totalPages - 1 && (
          <button
            onClick={goNext}
            disabled={isAnimating}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full
              bg-amber-100/80 hover:bg-amber-200 text-amber-800/60 hover:text-amber-900
              transition-colors duration-150 disabled:opacity-30"
            aria-label="Next page"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}

        {/* Page indicator dots */}
        {totalPages > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {Array.from({ length: totalPages }).map((_, i) => (
              <div
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors duration-200 ${
                  i === currentPage ? 'bg-amber-800/60' : 'bg-amber-800/20'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewspaperOverlay;
